import { useMutation, useQuery } from '@tanstack/react-query';
import type { HoldReason, WorkHold, WorkItem } from '@k-management/shared';
import { useState } from 'react';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { Field } from '../../components/Field.js';
import { apiRequest } from '../../lib/api.js';
import { formatDate } from '../../lib/format.js';
import { worklistKeys } from './treasury-ui.js';

export const WORK_KIND_LABELS: Record<WorkItem['kind'], string> = {
  movimento: 'Movimento',
  spesa_carta: 'Spesa con carta',
  fattura: 'Fattura',
  rata: 'Rata di una rateazione',
  autofattura: 'Autofattura',
};

/**
 * «Metti in sospeso» (3.00): an item that cannot be settled now, with the
 * reason, a note and the day to remind it. With a hold already, it changes it.
 */
export function HoldDialog({
  item,
  onClose,
  onSaved,
}: {
  item: WorkItem;
  onClose: () => void;
  onSaved: () => Promise<void> | void;
}) {
  const current = item.hold;
  const [reasonCode, setReasonCode] = useState(current?.reasonCode ?? '');
  const [note, setNote] = useState(current?.note ?? '');
  const [remindOn, setRemindOn] = useState(current?.remindOn ?? '');
  const reasons = useQuery({
    queryKey: [...worklistKeys.all, 'reasons'],
    queryFn: () => apiRequest<HoldReason[]>('/treasury/worklist/reasons'),
    staleTime: 300_000,
  });
  const save = useMutation({
    mutationFn: () => {
      const body = { reasonCode, note: note.trim() || null, remindOn: remindOn || null };
      return current
        ? apiRequest<WorkHold>(`/treasury/worklist/holds/${current.id}`, { method: 'PUT', body })
        : apiRequest<WorkHold>('/treasury/worklist/holds', {
            method: 'POST',
            body: { kind: item.kind, itemId: item.id, ...body },
          });
    },
    onSuccess: onSaved,
  });

  return (
    <ConfirmDialog
      title={`${WORK_KIND_LABELS[item.kind]} del ${formatDate(item.date)}`}
      confirmLabel={current ? 'Salva' : 'Metti in sospeso'}
      busy={save.isPending}
      disabled={!reasonCode}
      error={save.error?.message ?? reasons.error?.message}
      onConfirm={() => save.mutate()}
      onCancel={onClose}
    >
      <p className="muted small">{item.description}</p>
      <div className="form-grid">
        <Field label="Motivo">
          <select value={reasonCode} onChange={(event) => setReasonCode(event.target.value)}>
            <option value="">— scegli —</option>
            {(reasons.data ?? []).map((reason) => (
              <option key={reason.code} value={reason.code}>
                {reason.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Ricordamelo il">
          <input
            type="date"
            value={remindOn}
            onChange={(event) => setRemindOn(event.target.value)}
          />
        </Field>
      </div>
      <Field label="Nota">
        <textarea
          rows={3}
          maxLength={500}
          value={note}
          onChange={(event) => setNote(event.target.value)}
        />
      </Field>
      <p className="muted small">
        Quando l’elemento viene sistemato il sospeso si chiude da solo. Dopo la data torna in
        evidenza come «sospeso scaduto».
      </p>
    </ConfirmDialog>
  );
}
